import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { ToastContainer, ToastData, getDurationByType } from '../components/ui/ToastContainer';

type ToastInput = Omit<ToastData, 'id'>;

interface ToastContextType {
  showToast: (toast: ToastInput | string) => void;
  removeToast: (id: string) => void;
  removeAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((input: ToastInput | string) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Handle legacy string-only calls
    if (typeof input === 'string') {
      const newToast: ToastData = {
        id,
        title: input,
        variant: 'success',
        duration: getDurationByType('success'),
      };
      setToasts(prev => [...prev, newToast]);
      return;
    }

    // Handle new ToastInput format
    const newToast: ToastData = {
      ...input,
      id,
      duration: input.duration ?? getDurationByType(input.variant),
    };

    setToasts(prev => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, removeToast, removeAllToasts }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  // Create helper methods for easier migration
  const toast = context.showToast;

  const success = (title: string, description?: string, options?: Partial<ToastInput>) => {
    context.showToast({ title, description, variant: 'success', ...options });
  };

  const error = (title: string, description?: string, options?: Partial<ToastInput>) => {
    context.showToast({ title, description, variant: 'error', ...options });
  };

  const info = (title: string, description?: string, options?: Partial<ToastInput>) => {
    context.showToast({ title, description, variant: 'info', ...options });
  };

  const achievement = (title: string, description?: string, options?: Partial<ToastInput>) => {
    context.showToast({ title, description, variant: 'achievement', ...options });
  };

  return {
    toast,
    success,
    error,
    info,
    achievement,
    showToast: context.showToast,
    removeToast: context.removeToast,
    removeAllToasts: context.removeAllToasts,
  };
};
